import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { OBJECTIVES_EMPTY_SEARCH } from "@/router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { StatusBadge } from "@/components/shared/status-badge";
import { StatusTransitionSelect } from "@/components/shared/status-transition-select";
import { EmptyState } from "@/components/shared/empty-state";
import { ObjectiveInlineEditor } from "@/features/objectives/objective-inline-editor";
import { KeyResultCard } from "@/features/key-results/key-result-card";
import { CreateKeyResultDialog } from "@/features/key-results/create-key-result-dialog";
import { useObjective, useUpdateObjectiveStatus, useDeleteObjective } from "@/hooks/use-objectives";
import { useKeyResults } from "@/hooks/use-key-results";
import { useCurrentUser } from "@/hooks/use-current-user";
import { isTerminalStatus } from "@/lib/status-machine";
import { formatTimestamp } from "@/lib/date";
import { getEntityPermissions } from "@/lib/permissions";
import { useUsers } from "@/hooks/use-users";
import { isSupervisorOrAbove } from "@/hooks/use-current-user";
import { KudosList } from "@/features/kudos/kudos-list";
import { useFocusAreas } from "@/hooks/use-focus-areas";
import { useTactics } from "@/hooks/use-tactics";
import { useCollegeGoals } from "@/hooks/use-college-goals";
import { useCollegeStrategicPillars } from "@/hooks/use-college-strategic-pillars";
import { DeleteConfirmDialog } from "@/components/shared/delete-confirm-dialog";
import { ConfirmRevertDialog } from "@/components/shared/confirm-revert-dialog";
import { buildObjectiveLadder } from "@/lib/objective-ladder";
import { ObjectiveCoachPanel } from "@/features/okr-coach/detail-coach-panel";
import { useMemo, useState } from "react";
import type { Objective, KeyResult, ObjectiveStatus } from "@/types/entities";

function ObjectiveDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-9 w-64" />
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

interface ObjectiveHeaderProps {
  objective: Objective;
  canEdit: boolean;
  canDelete: boolean;
  canRevertToDraft: boolean;
  onStatusTransition: (status: ObjectiveStatus) => void;
  statusPending: boolean;
  onDeleteClick: () => void;
}

function ObjectiveHeader({ objective, canEdit, canDelete, canRevertToDraft, onStatusTransition, statusPending, onDeleteClick }: ObjectiveHeaderProps) {
  return (
    <>
      <div className="flex items-center gap-2">
        <Link to="/objectives" search={{ year: undefined, level: undefined, status: undefined, type: undefined, focus_area_id: undefined, owner_id: undefined, view: undefined }} className="text-sm text-[hsl(var(--muted-foreground))] hover:underline">
          Objectives
        </Link>
        <span className="text-sm text-[hsl(var(--muted-foreground))]">/</span>
        <span className="text-sm">{objective.title}</span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-3xl font-semibold">{objective.title}</h1>
          <StatusBadge status={objective.status} />
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <StatusTransitionSelect
              currentStatus={objective.status}
              onTransition={onStatusTransition}
              disabled={statusPending}
              canRevertToDraft={canRevertToDraft}
            />
          )}
          {canDelete && (
            <Button variant="destructive" size="sm" onClick={onDeleteClick}>
              Delete
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

interface KeyResultsSectionProps {
  keyResults: KeyResult[];
  isLoading: boolean;
  objectiveId: string;
  objectiveOwnerId: string;
  canAddKr: boolean;
  year?: number;
}

function KeyResultsSection({ keyResults, isLoading, objectiveId, objectiveOwnerId, canAddKr, year }: KeyResultsSectionProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Key Results</CardTitle>
          {canAddKr && <CreateKeyResultDialog objectiveId={objectiveId} objectiveOwnerId={objectiveOwnerId} year={year} />}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : keyResults.length === 0 ? (
          <EmptyState
            title="No Key Results"
            description="Add key results to track measurable outcomes for this objective."
          />
        ) : (
          <div className="space-y-3">
            {keyResults.map((kr) => (
              <KeyResultCard key={kr.id} keyResult={kr} year={year} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ObjectiveMetadata({ objective }: { objective: Objective }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Metadata</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Level</p>
          <Badge variant="secondary" className="mt-1 capitalize">{objective.level}</Badge>
        </div>
        <Separator />
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Type</p>
          <Badge variant="secondary" className="mt-1 capitalize">{objective.type}</Badge>
        </div>
        <Separator />
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Created</p>
          <p className="text-sm">{formatTimestamp(objective.created_at)}</p>
        </div>
        <Separator />
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Updated</p>
          <p className="text-sm">{formatTimestamp(objective.updated_at)}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function StrategicContextSection({ objective }: { objective: Objective }) {
  const { data: focusAreasData } = useFocusAreas();
  const { data: tacticsData } = useTactics();
  const { data: collegeGoalsData } = useCollegeGoals();
  const { data: pillarsData } = useCollegeStrategicPillars();

  const ladder = useMemo(
    () =>
      buildObjectiveLadder(objective.focus_area_ids, objective.tactic_ids, {
        focusAreas: focusAreasData?.data ?? [],
        tactics: tacticsData?.data ?? [],
        collegeGoals: collegeGoalsData?.data ?? [],
        pillars: pillarsData?.data ?? [],
      }),
    [objective.focus_area_ids, objective.tactic_ids, focusAreasData, tacticsData, collegeGoalsData, pillarsData],
  );

  if (!ladder) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Strategic Context</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Not aligned</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Strategic Context</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {ladder.pillar && (
          <div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">College Strategic Pillar</p>
            <Link
              to="/admin/college-strategy"
              className="text-sm font-medium text-[hsl(var(--primary))] hover:underline"
            >
              {ladder.pillar.name}
            </Link>
          </div>
        )}
        {ladder.pillar && ladder.goal && <Separator />}
        {ladder.goal && (
          <div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">College Objective</p>
            <Link
              to="/admin/college-strategy"
              className="text-sm font-medium text-[hsl(var(--primary))] hover:underline"
            >
              {ladder.goal.name}
            </Link>
          </div>
        )}
        {(ladder.pillar ?? ladder.goal) && <Separator />}
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">ITS Focus Area</p>
          <Link
            to="/admin/strategic-plans"
            className="text-sm font-medium text-[hsl(var(--primary))] hover:underline"
          >
            {ladder.focusArea.name}
          </Link>
        </div>
        {ladder.strategicInitiative && (
          <>
            <Separator />
            <div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">ITS Strategic Initiative</p>
              <Link
                to="/admin/strategic-plans"
                className="text-sm font-medium text-[hsl(var(--primary))] hover:underline"
              >
                {ladder.strategicInitiative.name}
              </Link>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}


export function ObjectiveDetailPage() {
  const { id } = useParams({ from: "/objectives/$id" });
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const { data, isLoading, error } = useObjective(id);
  const { data: krData, isLoading: krLoading } = useKeyResults({ objective_id: id });
  const needsHierarchyCheck = isSupervisorOrAbove(currentUser?.role);
  const { data: usersData } = useUsers(needsHierarchyCheck ? { limit: 100 } : { limit: 0 });
  const allUsers = usersData?.data ?? [];
  const updateStatus = useUpdateObjectiveStatus(id);
  const deleteObjective = useDeleteObjective();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);

  if (isLoading) return <ObjectiveDetailSkeleton />;

  if (error || !data) {
    return (
      <EmptyState
        title="Objective not found"
        description={error?.message ?? "The objective you are looking for does not exist."}
        actionLabel="Back to Objectives"
        onAction={() => void navigate({ to: "/objectives", search: OBJECTIVES_EMPTY_SEARCH })}
      />
    );
  }

  const objective = data.data;
  const keyResults = krData?.data ?? [];
  const { canEdit, canRevertToDraft } = getEntityPermissions({ currentUser, ownerId: objective.owner_id, users: allUsers });
  const canDelete = canEdit && objective.status === "draft";

  const handleStatusTransition = (status: ObjectiveStatus) => {
    if (status === "draft") {
      setRevertDialogOpen(true);
      return;
    }
    updateStatus.mutate(status);
  };

  return (
    <div className="space-y-6">
      <ObjectiveHeader
        objective={objective}
        canEdit={canEdit}
        canDelete={canDelete}
        canRevertToDraft={canRevertToDraft}
        onStatusTransition={handleStatusTransition}
        statusPending={updateStatus.isPending}
        onDeleteClick={() => setDeleteDialogOpen(true)}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent>
              <ObjectiveInlineEditor
                objective={objective}
                canEdit={canEdit}
                isDepartmentHead={allUsers.find((u) => u.id === objective.owner_id)?.department_head ?? false}
              />
            </CardContent>
          </Card>

          {objective.status === "draft" && (
            <ObjectiveCoachPanel
              statement={objective.title}
              level={objective.level}
              type={objective.type}
            />
          )}

          <KeyResultsSection
            keyResults={keyResults}
            isLoading={krLoading}
            objectiveId={objective.id}
            objectiveOwnerId={objective.owner_id}
            canAddKr={canEdit && !isTerminalStatus(objective.status)}
            year={objective.year}
          />
          <KudosList objectiveId={objective.id} />
        </div>

        <div className="space-y-6">
          <ObjectiveMetadata objective={objective} />
          <StrategicContextSection objective={objective} />
        </div>
      </div>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Objective"
        description="Are you sure you want to delete this objective? This action cannot be undone."
        onConfirm={() => {
          deleteObjective.mutate(objective.id, {
            onSuccess: () => void navigate({ to: "/objectives", search: OBJECTIVES_EMPTY_SEARCH }),
          });
        }}
        isPending={deleteObjective.isPending}
      />

      <ConfirmRevertDialog
        open={revertDialogOpen}
        onOpenChange={setRevertDialogOpen}
        entityLabel="objective"
        onConfirm={() => {
          updateStatus.mutate("draft", {
            onSuccess: () => setRevertDialogOpen(false),
          });
        }}
        isPending={updateStatus.isPending}
      />
    </div>
  );
}
